import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { QRCodeModule } from 'angularx-qrcode';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AppDownloadService, StoreOs } from '../../../services/app-download.service';
import { GtmAnalyticsService } from '../../../services/gtm-analytics.service';
import { AppDownloadDialogComponent } from './app-download-dialog.component';

/**
 * "Keep tracking from your phone" — the app-download surface
 * (app-download spec, Danna 2026-09-17).
 *
 * Two shapes, one component:
 *  - `variant="card"` sits at the END of her report, under the last card. On a
 *    phone it shows the single badge for that device; on a desktop it shows a
 *    code to scan next to both badges, because a desktop cannot install.
 *  - `variant="row"` is the quiet row in the profile. On a desktop it opens the
 *    same code in a small dialog.
 *
 * There is deliberately no "not now": the card never covers anything, so a
 * dismiss button would only add a second action next to the one that matters.
 */
@Component({
  selector: 'app-download-card',
  standalone: true,
  imports: [CommonModule, QRCodeModule, TranslatePipe],
  templateUrl: './app-download-card.component.html',
  styleUrls: ['./app-download-card.component.scss'],
})
export class AppDownloadCardComponent {
  @Input() variant: 'card' | 'row' = 'card';
  /** Where this instance lives, so the funnel can tell the three apart. */
  @Input() placement: 'report' | 'profile' | 'home' | 'routine' = 'report';

  constructor(
    public dl: AppDownloadService,
    private dialog: MatDialog,
    private gtm: GtmAnalyticsService,
  ) {}

  /** A badge was used. The link itself still does the navigating. */
  track(store: StoreOs | 'qr'): void {
    this.gtm.trackAppStoreClicked(this.placement, store);
  }

  get show(): boolean {
    return this.dl.available;
  }

  get os(): StoreOs | null {
    return this.dl.deviceOs;
  }

  get isDesktop(): boolean {
    return this.dl.isDesktop;
  }

  storeUrl(os: StoreOs): string {
    return this.dl.storeUrl(os);
  }

  badgeSrc(os: StoreOs): string {
    return this.dl.badgeSrc(os);
  }

  get qrUrl(): string {
    return this.dl.qrUrl;
  }

  /** Profile row: straight to the store on a phone, the code on a desktop. */
  openRow(): void {
    const os = this.os;
    this.track(os || 'qr');
    if (os) {
      window.open(this.storeUrl(os), '_blank', 'noopener');
      return;
    }
    this.dialog.open(AppDownloadDialogComponent, {
      panelClass: 'app-download-dialog-pane',
      autoFocus: false,
    });
  }
}
